import type { ContinuityRuntime } from "@/lib/behavior/viewModels/buildContinuityRuntime";
import type {
  CoachSessionPrepVeeraBrief,
  CoachVeeraPrepEvidenceRef,
} from "@/lib/behavior/viewModels/buildCoachSessionPrepVeeraBrief";

export interface LeaderArcResource {
  headline: string;
  repeatedPatterns: string[];
  whatChanged: string[];
  strengths: string[];
  unresolvedTension: string;
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  confidenceLabel: CoachSessionPrepVeeraBrief["confidenceLabel"];
  confidenceNote: string;
  early: boolean;
}

function dedupeStrings(values: Array<string | null | undefined>, limit = 4): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value?.replace(/\s+/g, " ").trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
    if (result.length >= limit) break;
  }
  return result;
}

function pickHeadline(params: {
  early: boolean;
  repeatedPatterns: string[];
  whatChanged: string[];
  strengths: string[];
}): string {
  if (params.early) {
    return "This is still an early arc: more evidence is needed before treating the pattern as stable.";
  }

  const changed = params.whatChanged[0];
  const repeated = params.repeatedPatterns[0];
  const strength = params.strengths[0];

  if (changed && repeated) {
    return `A developing arc is emerging: ${changed} while ${repeated.toLowerCase()}`;
  }
  if (changed && strength) {
    return `A developing throughline is emerging: ${changed} and ${strength.toLowerCase()}`;
  }
  return "A developing pattern arc is emerging from the recent coaching evidence.";
}

function pickUnresolvedTension(params: {
  brief: CoachSessionPrepVeeraBrief;
  continuity: ContinuityRuntime;
  early: boolean;
}): string {
  if (params.brief.unresolvedTension) return params.brief.unresolvedTension;
  if (params.continuity.next[0]) return params.continuity.next[0];
  if (params.early) {
    return "There is not enough longitudinal evidence yet to name a stable tension with confidence.";
  }
  return "The next session should test whether the strongest recent shift holds under pressure.";
}

export function buildLeaderArcResource(params: {
  brief: CoachSessionPrepVeeraBrief;
  continuity: ContinuityRuntime;
}): LeaderArcResource {
  const repeatedPatterns = dedupeStrings([
    ...params.brief.whatPersists,
    ...params.continuity.carryForward,
  ], 3);
  const whatChanged = dedupeStrings([
    ...params.brief.whatChanged,
    ...params.continuity.changed,
  ], 3);
  const strengths = dedupeStrings([
    ...params.brief.observedShifts,
  ], 3);
  const evidenceRefs = params.brief.evidenceRefs.slice(0, 4);

  const early = evidenceRefs.length === 0
    || (repeatedPatterns.length === 0 && whatChanged.length === 0)
    || (params.brief.confidenceLabel === "low" && strengths.length <= 1);

  const confidenceLabel = early ? "low" : params.brief.confidenceLabel;
  const confidenceNote = early
    ? "This is still an early arc built from limited evidence. Gather more longitudinal signal before treating it as stable."
    : params.brief.confidenceNote;

  return {
    headline: pickHeadline({
      early,
      repeatedPatterns,
      whatChanged,
      strengths,
    }),
    repeatedPatterns: repeatedPatterns.length > 0
      ? repeatedPatterns
      : ["No repeated pattern is stable yet; treat this as a live hypothesis."],
    whatChanged: whatChanged.length > 0
      ? whatChanged
      : ["No clear shift is supported yet beyond the latest evidence."],
    strengths: strengths.length > 0
      ? strengths
      : ["A durable strength has not surfaced yet from the available prep evidence."],
    unresolvedTension: pickUnresolvedTension({
      brief: params.brief,
      continuity: params.continuity,
      early,
    }),
    evidenceRefs,
    confidenceLabel,
    confidenceNote,
    early,
  };
}
